import { ReactNode } from 'react';

// Updated types to match the JSON payload and a small mapper to normalize to camelCase

export interface TeamApi {
    team_id: number;
    team_name: string;
    account_number: string[] | null;
    total_balance: string;
    total_trade_accounts: number;
    members: TeamMemberApi[];
    trade_accounts: TradeAccount[];
    total_trade_account: number;
}

export interface TeamMemberApi {
    user_id: number;
    role: string;
    status: string;
    account_number: string | null;
    total_balance: string;
    joined_at: string; // ISO timestamp
}

// Frontend-friendly (normalized) shapes
export interface Team {
    id: number;
    name: string;
    description?: string;
    membersCount: number;
    members: TeamMember[];
    membersSummary?: string; // e.g. "2 accepted, 1 pending"
    createdDate?: string; // e.g. "01/01/2025"
    isLeader?: boolean;
    total_balance: string;
    trade_accounts: TradeAccount[];
}

export interface TeamMember {
    user_id: number;
    user_name?: {
        avatar_url?: string;
        first_name: string;
        last_name: string;
        fullname?: string;
    };
    role: string;
    status: string;
    email: string;
    account_number: string | null;
    invitation_status?: string;
    totalBalance: string;
    balance?: number;
    join_date: string;
}

export interface TeamMemberSimple {
    name: string;
    email: string;
    role: 'Leader' | 'Member' | string;
    status: 'accepted' | 'pending' | string;
}
export type TeamMembers = TeamMemberSimple[];

export interface InvitationItem {
    name: string;
    email: string;
    status: 'pending' | 'accepted' | string;
}
export type Invitations = InvitationItem[];

export interface TradingAccountItem {
    accountName: string;
    platform: string;
    status: 'live' | 'demo' | string;
    balance: string;
    sharedBy?: string;
    date: string; // ISO or formatted date string
    eyeIcon?: boolean;
}
export type TradingAccounts = TradingAccountItem[];

export interface PendingInviteApi {
    team_id: number;
    team_name: string;
    team_member: number;
    team_mentor: string;
    account_number: string | null;
    target_user_id?: number;
    target_email?: string;
    first_name?: string;
    last_name?: string;
    is_personal?: boolean;
    role: string;
    status: string;
    invited_at: string; // ISO timestamp
    respond_url: string;
}

export interface PendingInvite {
    teamId: number;
    teamName: string;
    team_mentor?: string;
    team_member: number;
    accountNumber: string | null;
    target_user_id?: number;
    target_email?: string;
    first_name?: string;
    last_name?: string;
    is_personal?: boolean;
    role: string;
    status: string;
    invitedAt: string;
    respondUrl: string;
}

export type PendingInvites = PendingInvite[];

/** Optional mapper from API -> normalized frontend shape */
export const mapPendingInvite = (api: PendingInviteApi): PendingInvite => ({
    teamId: api.team_id,
    teamName: api.team_name,
    team_member: api.team_member,
    team_mentor: api.team_mentor,
    accountNumber: api.account_number,
    target_user_id: api.target_user_id,
    target_email: api.target_email,
    is_personal: api.is_personal,
    first_name: api.first_name,
    last_name: api.last_name,
    role: api.role,
    status: api.status,
    invitedAt: api.invited_at,
    respondUrl: api.respond_url,
});
// Mapper from API -> normalized frontend types

export interface StatItemTeamDetail {
    title: string;
    value: number | string;
    icon: ReactNode;
    textColor: string;
}

export interface Stat {
    title: string;
    value: number | string;
    icon: ReactNode;
    bgColor: string;
}

export interface RecentTeamItem {
    id: number;
    name: string;
    members: number;
    status: string;
}

export interface PendingActionItem {
    title: string;
    detail: string;
}

export interface TeamsListItem {
    id: number;
    name: string;
    description?: string;
    members: number;
    createdDate: string;
    isLeader?: boolean;
    total_trade_account: number;
    status?: string;
}

export interface MyJoinRequest {
    teamId: number;
    teamName: string;
    status: "Pending" | "Approved" | "Rejected";
    requestedAt: string;
}

export interface TradeAccountApi {
    id: number;
    user_id: number;
    account_number: string;
    account_password: string | null;
    platform: string;
    balance: number | string;
    nickname?: string | null;
    broker?: string | null;
    type: 'live' | 'demo' | string;
    created_at: string;
    updated_at: string;
}

export interface TradeAccount {
    account_number: string;        // from "account_number"
    nickname?: string | null;    // from "nickname"
    balance: number | string;    // from "balance"
    platform: string;            // from "platform"
    share_date?: string;          // from "share_date" (ISO timestamp)
    share_by?: {
        fullname: string;
        first_name: string;
        last_name: string;
    }
    can_view_history: boolean;
}
export interface TeamDetailStats {
    total_team: number;
    total_team_member: number;
    total_trading_accounts: number;
    total_balance: number;
}

export interface QRInviteResponse {
    success: boolean;
    qr_invite_url: string;
    signature: string;
    expires: string;
}
export interface TreemapSeries {
    first_name: string;
    last_name: string;
    account_number: string;
    account_size: number;
    account_balance: number;
    pnl_percent: number;
    is_danger: boolean;
    drawdown?: number;
}

export interface TmItem {
    name: string;
    accountNumber: string;
    value: number;
    sizeK: number;
    pnl: number;
    dd: number;
    fullName: string;
    drawdown?: number;
    account_size?: number;
    account_balance?: number;
}

// Ensure your squarify algorithm passes custom attributes down to TmRect
export interface TmRect extends TmItem {
    rx: number;
    ry: number;
    rw: number;
    rh: number;
}

export interface Treemap {
    total_size: number;
    series: TreemapSeries[];
}

export interface Phases {
    total: number;
    funded: number;
    challenge: number;
    verification: number;
    newbie: number;
}

export interface Trades {
    total: number;
    win: number;
    loss: number;
    breakeven: number;
}

export interface Distribution {
    phases: Phases;
    trades: Trades;
}

export interface ChartDataSet {
    account_numbers: string[];
    first_name: string[];
    last_name: string[];
    data: number[];
}

export interface ViolationsDataSet {
    first_name: string[];
    last_name: string[];
    account_numbers: string[];
    no_sl: number[];
    missing_journal: number[];
}

export interface MemberCharts {
    pass_probability: ChartDataSet;
    losing_trades: ChartDataSet;
    violations: ViolationsDataSet;
}



export interface Performance {
    total_trades: number;
    profit_factor: number;
    winrate: number;
    expectancy: number;
    average_rr: number;
    monthly_return: number;
    max_drawdown: number;
    sharpe_ratio: number;
    gross_profit: number;
    gross_loss: number;
}

export interface RiskManagement {
    avg_risk_per_trade: number;
    max_consecutive_losses: number;
    monthly_loss_percent: number;
    position_sizing_accuracy: number;
}

export interface JournalKpis {
    fomo_rate: number;
    journal_completion_rate: number;
}

export interface FundingProgress {
    phase?: string;
    evaluation_status?: string;
    target_percent?: number;
    current_growth?: number;
    trading_days?: number;
    max_drawdown?: number;
    daily_drawdown?: number;
    progress_percent?: number;
    journal_score?: number;
    error?: string;
}

export interface MemberMetrics {
    user_id: number;
    fullname: string;
    role: string;
    account_number: string;
    performance: Performance;
    risk_management: RiskManagement;
    journal_kpis: JournalKpis;
    funding_progress: FundingProgress;
}

export interface TeamDetailTrackingTable {
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    account_number: string;
    account_size: number;
    phase: string;
    pnl_week: number;
    drawdown: number;
    roadmap_pnl: number;
    no_sl_count: number;
    missing_journal: number;
    health_status: string;
    platform?: string;
    alerts: string[];
}

export interface TeamDetailChartDataApi {
    success: boolean;
    data?: {
        treemap: Treemap;
        distribution: Distribution;
        member_charts: MemberCharts;
    };
    message?: string;
}

export interface TeamDetailMetricsApi {
    success: boolean;
    message?: string;
    team_id: string;
    period: {
        start_date: string;
        end_date: string;
    };
    data?: MemberMetrics[];
}

export interface TeamDetailOverviewApi {
    success: boolean;
    message?: string;
    team_id: string;
    data?: {
        total_members: number;
        growing_accounts: number;
        at_risk_accounts: number;
        funded_accounts: number;
        passing_potential: number;
        losing_trades: number;
        sl_violations: number;
        missing_journal: number;
    };
}

export interface TeamDetailTrackingTableApi {
    success: boolean;
    message?: string;
    data?: TeamDetailTrackingTable[];
}

export type MyTradeAccounts = TradeAccountApi[];
export type TradeAccounts = TradeAccount[];

// optional convenience types
export type StatsTeams = StatItemTeamDetail[];
export type RecentTeams = RecentTeamItem[];
export type PendingActions = PendingActionItem[];
export type TeamsList = TeamsListItem[];

export type TeamDetailTab = "dashboard" | "accounts" | "members";

export interface MockTeamPerformanceItem {
    id: string;
    name: string;
    stage: "FUNDED" | "PAYOUT" | "VERIFY" | "CHALLENGE";
    balance: string;
    growth: number;
    drawdown: number;
    lossTrades: number;
    slRate: number;
    avgR: string;
    health: "Tốt" | "Trung bình" | "Nguy hiểm";
    warning: string;
}

export interface MockTradeAccountItem {
    id: number;
    name: string;
    platform: "MT4" | "MT5" | "CTRADER";
    accountNumber: string;
    balance: string;
    sharedBy: string;
    date: string;
}

export interface MockTeamMemberItem {
    id: string;
    name: string;
    role: "Trưởng nhóm" | "Phó nhóm" | "Thành viên";
    status: "approved" | "pending";
    joinDate: string;
}
